import React, { useState, useEffect, useCallback, useContext } from "react";
import PropTypes from "prop-types";
import { useCartContext } from "~/assets/js/global/cartContext";
import {
    Container,
    IngredientList,
    Chips,
    OuterContainer,
    Left,
    Right,
    RightTop,
    RightTopElement,
    ChosenOnes,
    SmallWidgetContainer,
    Bottom,
    StickyRight,
    RightContent,
    BottomButtonContainer,
    BottomRightContainer,
    CatName,
    MobileCart,
    MobileMiniCart,
    CatSubheader,
    AddToMobileCart,
    SubheaderNotice,
} from "./style";
import Portal from "~/assets/js/components/Portal";
import Widget from "~/assets/js/components/Widget";
import Alert from "~/assets/js/components/Alert";
import Chip from "~/assets/js/components/Chip";
import SearchBar from "~/assets/js/components/KonfiguratorSearchBar";
import {
    createDescription,
    formatFloat,
    formatPrice,
    getTotalEnergy,
} from "~/assets/js/Helpers/util";
import InlineNotification from "~/assets/js/components/InlineNotification";

import GlutenFree from "~/static/images/icons/gluten-free.svg";
import LowCarb from "~/static/images/icons/low-carb.svg";
import SugarFree from "~/static/images/icons/sugar-free.svg";
import Vegan from "~/static/images/icons/vegan.svg";
import Vegetarian from "~/static/images/icons/vegetarian.svg";

import Fork from "~/static/images/icons/fork.svg";
import Knife from "~/static/images/icons/knife.svg";
import Globe from "~/static/images/icons/globe.svg";
import Water from "~/static/images/icons/water.svg";
import Basket from "~/static/images/icons/basket.svg";

import Bowl from "~/static/images/bowl.png";
import Salad from "~/static/images/potti.png";

import AlertTriangle from "~/static/images/icons/alert-triangle.svg";
import Cross from "~/static/images/icons/Cross.svg";
import Dressing from "~/static/images/icons/Dressing.svg";

import {
    getIngredients,
    useIngredientsStock,
} from "~/assets/js/Api/ingredientsApi";
import SmallWidget from "~/assets/js/components/SmallWidget";
import DefaultButton from "~/assets/js/components/Button/DefaultButton";
import Affix from "~/assets/js/components/Affix";
import useViewport from "~/assets/js/Helpers/hooks/useViewport";
import BottomDweller from "~/assets/js/components/BottomDweller";
import {
    isAnythingElseFilter,
    isBaseFilter,
    isBowlBaseFilter,
    isDressingFilter,
    isSauceFilter,
    isExtraFilter,
    isPremiumFilter,
    isRamenBaseFilter,
    isSaladBaseFilter,
    isStandardFilter,
    isToppingFilter,
} from "./filters";
import {
    DeliveryContext,
    DeliveryProvider,
} from "~/assets/js/models/DeliveryContext";
import AddToCartSuccess from "../../ProductDetails/components/Notifications/AddToCartSuccess";
import { viewPage, viewProduct } from "~/assets/js/Api/eventsApi";
import useGreenClub from "./useGreenClub";
import FullPageLoader from "~/assets/js/components/Loaders/FullPageLoader";
import GreenClubTag from "~/assets/js/components/GreenClub/GreenClubTag";
import { calculateFranchisePrice } from "~/assets/js/Helpers/util";
import i18n from "i18next";
import { getProduct } from "~/assets/js/Api/storeApi";

const MAX_INGREDIENTS = { salad: 10, bowl: 10, ramen: 8 };
const MIN_INGREDIENTS = { salad: 3, bowl: 3, ramen: 3 };
const MAX_SINGLE = { salad: 5, bowl: 5, ramen: 5 };

export const DRESSING_CAT = "dressing";
export const SAUCE_CAT = "sauce";
export const BASE_CAT = "basis";
export const STANDARD_CAT = "standard";
export const TOPPING_CAT = "topping";
export const PREMIUM_CAT = "premium";
export const EXTRA_CAT = "extras";

const includedIn = {
    salad: {
        [BASE_CAT]: 1,
        [STANDARD_CAT]: 2,
        [EXTRA_CAT]: 1,
        [PREMIUM_CAT]: 1,
        [TOPPING_CAT]: 1,
        [DRESSING_CAT]: 1,
        [SAUCE_CAT]: 0,
    },
    bowl: {
        [BASE_CAT]: 2,
        [STANDARD_CAT]: 2,
        [EXTRA_CAT]: 1,
        [PREMIUM_CAT]: 2,
        [TOPPING_CAT]: 1,
        [DRESSING_CAT]: 0,
        [SAUCE_CAT]: 0,
    },
    ramen: {
        [BASE_CAT]: 1,
        [STANDARD_CAT]: 3,
        [EXTRA_CAT]: 0,
        [PREMIUM_CAT]: 0,
        [TOPPING_CAT]: 2,
        [DRESSING_CAT]: 0,
        [SAUCE_CAT]: 1,
    },
};

export const SCROLL_TIMEOUT = 100;
const HIDE_DWELLER_ON_SCROLL = false;

const ERROR = {
    MAX_TOTAL_INGREDIENTS: 1,
    MAX_SINGLE_INGREDIENTS: 2,
    MIN_TOTAL_INGREDIENTS: 3,
    NO_BASE_SELECTED: 4,
    NO_DRESSING_SELECTED: 5,
    ADD_TO_BASKET_ERROR: 6,
};

const CHIPS = [
    { value: "vegetarisch", text: "Vegetarisch", icon: Vegetarian },
    { value: "vegan", text: "Vegan", icon: Vegan },
    { value: "low-carb", text: "Low Carb", icon: LowCarb },
    { value: "zuckerfrei", text: "Zuckerarm", icon: SugarFree },
    { value: "glutenfrei", text: "Glutenfrei", icon: GlutenFree },
];

const productTypes = {
    salad: { base: "basis", isTypeBaseFilter: isSaladBaseFilter, image: Salad },
    bowl: {
        base: "basis-bowl",
        isTypeBaseFilter: isBowlBaseFilter,
        image: Bowl,
    },
    ramen: {
        base: "basis-ramen",
        isTypeBaseFilter: isRamenBaseFilter,
        image: "/wp-content/plugins/pottsalat/static/images/ramen.webp",
    },
};

let scrollTimeout = null;

const IngredientPage = ({ productId, config, productType }) => {
    let { storeId } = useContext(DeliveryContext);
    if (!storeId) storeId = 0;
    const { cart, actions, cartError, numberOfDiscountedProducts } =
        useCartContext();
    let franchiseId = cart && cart.franchise_id ? cart.franchise_id : 0;
    const productBase = getProduct(productId, franchiseId, storeId);
    const { greenClubLoading, hasGreenClubDiscount, greenClubPrice } =
        useGreenClub(productId, franchiseId, numberOfDiscountedProducts);

    const [ingredients, setIngredients] = useState([]);
    const [filteredIngredients, setFilteredIngredients] = useState([]);
    const [error, setError] = useState(null);
    const [chipsSelected, setChipsSelected] = useState([]);
    const [selectedIngredientsWithDetails, setSelectedIngredientsWithDetails] =
        useState([]);
    const [selectedIngredients, setSelectedIngredients] = useState([]);
    const [addedToCartPopupOpen, setAddedToCartPopupOpen] = useState(false);
    const [loading, setLoading] = useState([
        "overlays",
        "ingredients",
        "stocks",
    ]);
    const configuratorRules = {
        included: includedIn[productType],
        maxIngredients: MAX_INGREDIENTS[productType],
        minIngredients: MIN_INGREDIENTS[productType],
        maxSingle: MAX_SINGLE[productType],
    };
    const isRamen = productType === "ramen";
    const isSauceProduct = isRamen;

    const [dwellerExpanded, setDwellerExpanded] = useState(false);
    const [showMiniCheckout, setShowMiniCheckout] = useState(true);
    const [hideIngredients, setHideIngredients] = useState(false);

    const viewport = useViewport();

    const chippedIngredients =
        chipsSelected.length > 0
            ? filteredIngredients.filter(
                  (ing) =>
                      !chipsSelected.find(
                          (chip) => !ing.attributes.find((tag) => tag === chip)
                      )
              )
            : filteredIngredients;

    const totalIngredients = selectedIngredients
        .filter(
            (ing) =>
                !isBaseFilter(ing.ingredient) &&
                !isDressingFilter(ing.ingredient) &&
                !isSauceFilter(ing.ingredient)
        )
        .reduce((curr, prev) => curr + prev.amount, 0);

    const price = formatPrice(
        selectedIngredientsWithDetails
            .filter((ing) => !ing.isFree || isBaseFilter(ing.ingredient))
            .reduce((prev, curr) => {
                // Use GC discount price on BASE if GC is enabled
                if (isBaseFilter(curr.ingredient)) {
                    return prev;
                }

                return (
                    prev +
                    curr.amount *
                        calculateFranchisePrice(storeId, curr.ingredient)
                );
            }, parseFloat(productBase.product?.value_gross) ?? 0 * (hasGreenClubDiscount ? greenClubPrice : 1))
    );

    const ingredientsInStock = useIngredientsStock(storeId);

    useEffect(() => {
        if (storeId === 0 || loading.find((x) => x === "stocks")) return;
        ingredientsInStock.mutateIngredientsStock();
    }, [storeId]);

    useEffect(() => {
        setTimeout(() => {
            viewProduct(productId, productType);
            viewPage(document.title);
        }, 500);
    }, []);

    useEffect(() => {
        if (config) {
            global.apiUrl = config.url;
        }
    }, [config]);

    useEffect(() => {
        if (ingredientsInStock.isLoading) {
            setLoading((old) => [...old, "stocks"]);
        } else {
            setLoading((old) => old.filter((x) => x !== "stocks"));
        }
    }, [ingredientsInStock.isLoading]);

    useEffect(() => {
        // wait time until dweller is shown
        setTimeout(() => {
            setLoading((old) => old.filter((x) => x !== "overlays"));
        }, 1000);
    }, []);

    const stocks = ingredientsInStock.isLoading
        ? []
        : [
              ...ingredientsInStock.bases,
              ...ingredientsInStock.extras,
              ...ingredientsInStock.dressings,
              ...ingredientsInStock.sauces,
          ];

    const handleAddToCart = () => {
        setDwellerExpanded(false);
        // check if user added a base
        if (!selectedIngredients.find((ing) => isBaseFilter(ing.ingredient))) {
            setError(ERROR.NO_BASE_SELECTED);
            return;
        }

        // check if user added at least 3 ingredients
        if (
            selectedIngredients
                .filter(
                    (ing) =>
                        !isBaseFilter(ing.ingredient) &&
                        !isDressingFilter(ing.ingredient)
                )
                .reduce((prev, curr) => prev + curr.amount, 0) < 3
        ) {
            setError(ERROR.MIN_TOTAL_INGREDIENTS);
            return;
        }

        // check if user added a dressing
        if (
            !selectedIngredients.find(
                (ing) =>
                    isDressingFilter(ing.ingredient) ||
                    isSauceFilter(ing.ingredient)
            ) &&
            (configuratorRules.included[SAUCE_CAT] > 0 ||
                configuratorRules.included[SAUCE_CAT] > 0)
        ) {
            setError(ERROR.NO_DRESSING_SELECTED);
            return;
        }

        // everything is fine
        addProductToBasket();
    };

    const alertFromError = () => {
        const onClose = () => setError(null);
        switch (error) {
            case ERROR.MAX_TOTAL_INGREDIENTS: {
                return (
                    <Alert
                        header={`Die ${i18n.t(
                            "configurator.maxTotalIngredients." + productType
                        )} wird zu voll`}
                        image={Cross}
                        text={
                            <>
                                Wir müssen deine{" "}
                                {i18n.t(
                                    "configurator.maxTotalIngredients." +
                                        productType
                                )}{" "}
                                noch zubekommen. Mehr als{" "}
                                <b>
                                    {configuratorRules.maxIngredients} Zutaten
                                    kannst du nicht auswählen
                                </b>
                                .
                            </>
                        }
                        onClose={onClose}
                    />
                );
            }
            case ERROR.MAX_SINGLE_INGREDIENTS:
                return (
                    <Alert
                        header={"Zu viele von einer Zutat"}
                        image={Cross}
                        text={
                            <>
                                Du kannst nicht mehr als{" "}
                                <b>{configuratorRules.maxSingle}</b> von einer
                                Zutat auswählen.
                            </>
                        }
                        onClose={onClose}
                    />
                );
            case ERROR.NO_BASE_SELECTED: {
                return (
                    <Alert
                        header={`Du brauchst mindestens eine ${i18n.t(
                            "configurator.base." + productType
                        )}-Basis`}
                        image={AlertTriangle}
                        text={
                            <>
                                Wähle bitte mindestens eine{" "}
                                {i18n.t("configurator.base." + productType)}
                                -Basis aus den Zutaten aus.
                            </>
                        }
                        onClose={onClose}
                    />
                );
            }
            case ERROR.MIN_TOTAL_INGREDIENTS: {
                return (
                    <Alert
                        header={`Mindestens ${configuratorRules.minIngredients} Zutaten wählen`}
                        image={Cross}
                        text={
                            <>
                                Wir brauchen für deine{" "}
                                {i18n.t(
                                    "configurator.container." + productType
                                )}{" "}
                                mindestens {configuratorRules.minIngredients}{" "}
                                Zutaten. Bitte wähle noch etwas aus.
                            </>
                        }
                        onClose={onClose}
                    />
                );
            }
            case ERROR.NO_DRESSING_SELECTED:
                return (
                    <Alert
                        header={`Möchtest du wirklich ohne ${
                            isSauceProduct ? "Sauce" : "Dressing"
                        } weiter?`}
                        image={Dressing}
                        text={
                            <>
                                Wir haben viele leckere{" "}
                                {i18n.t(
                                    "configurator.dressings." + productType
                                )}
                                , die super zu{" "}
                                {i18n.t(
                                    "configurator.noDressingSelected." +
                                        productType
                                )}{" "}
                                passen würden.
                            </>
                        }
                        buttons={[
                            <DefaultButton
                                key={1}
                                onClick={() => {
                                    setError(null);
                                    const dressingCat = document.getElementById(
                                        `cat-${DRESSING_CAT}`
                                    );
                                    if (dressingCat) {
                                        window.scrollTo({
                                            top:
                                                dressingCat.getBoundingClientRect()
                                                    .top +
                                                window.pageYOffset -
                                                60,
                                            behavior: "smooth",
                                        });
                                    }
                                }}
                                appearance="primary"
                                block>
                                {i18n.t("configurator.dressing." + productType)}{" "}
                                hinzufügen
                            </DefaultButton>,
                            <DefaultButton
                                key={2}
                                onClick={() => {
                                    setError(null);
                                    addProductToBasket();
                                }}
                                appearance="default"
                                block>
                                Ohne{" "}
                                {i18n.t("configurator.dressing." + productType)}{" "}
                                weiter
                            </DefaultButton>,
                        ]}
                        onClose={onClose}
                    />
                );
            case ERROR.ADD_TO_BASKET_ERROR:
                return (
                    <Alert
                        header={"Fehler"}
                        image={Cross}
                        text={
                            <>
                                Hier ist leider etwas schiefgelaufen. Bitte
                                probiere es noch einmal.
                            </>
                        }
                        onClose={onClose}
                    />
                );
        }
    };

    const structureIngredients = (selectedIngredients) => {
        let structuredIngredients = [];

        selectedIngredients.map((ingredient) => {
            //let quantity = selectedIngredients.filter(countedIngredient => countedIngredient.product_id === ingredient.product_id).length;
            structuredIngredients.push({
                product_id: ingredient.ingredient.product_id,
                quantity: ingredient.amount,
            });
        });
        return structuredIngredients;
    };

    const addProductToBasket = () => {
        setLoading((old) => [...old, "add-to-cart"]);

        actions
            .addConfigurableProductToCart(
                productId,
                1,
                hasGreenClubDiscount ? 1 : 0,
                structureIngredients(selectedIngredients)
            )
            .then(() => {
                if (!cartError) {
                    setSelectedIngredients([]);
                    setAddedToCartPopupOpen(true);
                } else {
                    setError(ERROR.ADD_TO_BASKET_ERROR);
                }
            })
            .catch(() => {
                setError(ERROR.ADD_TO_BASKET_ERROR);
            })
            .finally(() => {
                setLoading((old) => old.filter((x) => x !== "add-to-cart"));
            });
    };

    // this handles onScroll events
    const scrollHandler = () => {
        if (HIDE_DWELLER_ON_SCROLL && !viewport.isDesktop) {
            if (scrollTimeout !== null) {
                const dweller = document.getElementById("bottom-dweller");
                if (dweller) dweller.style.transform = "translateY(150px)";
                clearTimeout(scrollTimeout);
            }
            scrollTimeout = setTimeout(() => {
                const dweller = document.getElementById("bottom-dweller");
                if (dweller) dweller.style.transform = "translateY(0)";
            }, SCROLL_TIMEOUT);
        }
        const stickyRightContainer = document.getElementById("sticky-right");
        const leftContainer = document.getElementById("left");
        const bottomRightContainer = document.getElementById(
            "bottom-right-container"
        );

        if (!stickyRightContainer || !leftContainer || !bottomRightContainer)
            return;

        const topOffset = Math.max(0, leftContainer.offsetTop - window.scrollY);
        const bottomOffset = Math.max(
            0,
            window.innerHeight +
                window.scrollY -
                (leftContainer.offsetTop + leftContainer.clientHeight) -
                10
        );
        stickyRightContainer.style.height = `calc(100vh - ${topOffset}px - ${bottomOffset}px)`;
        stickyRightContainer.style.maxHeight = `calc(100vh - ${topOffset}px - ${bottomOffset}px)`;
        bottomRightContainer.style.bottom = `${bottomOffset}px`;
        stickyRightContainer.style.paddingBottom = `${
            bottomRightContainer.clientHeight + 30
        }px`;
    };

    useEffect(() => {
        const listener = document.addEventListener(
            "scroll",
            scrollHandler,
            false
        );
        scrollHandler();
        return () => document.removeEventListener("scroll", listener);
    }, [scrollHandler]);

    useEffect(() => {
        setFilteredIngredients(ingredients);
    }, [ingredients]);

    useEffect(() => {
        getIngredients(storeId)
            .then((data) => {
                // get stocks for retrieved ingredients
                setLoading((old) => old.filter((x) => x !== "ingredients"));
                // set ingredients and filter out bowl or salad bases
                setIngredients(
                    data.products
                        .sort((a, b) => (a.name > b.name ? -1 : 1))
                        .sort((a, b) =>
                            a.sorting_priority > b.sorting_priority ? -1 : 1
                        )
                        .filter((ing) => {
                            if (
                                isBaseFilter(ing) &&
                                !productTypes[productType].isTypeBaseFilter(ing)
                            ) {
                                return false;
                            }
                            //filter out sauces/dressings
                            if (
                                isSauceProduct &&
                                ing.categories.includes("dressing")
                            )
                                return false;
                            if (
                                !isSauceProduct &&
                                ing.categories.includes("sauce")
                            )
                                return false;
                            if (
                                !!ing.available_in_configurators &&
                                !ing.available_in_configurators.includes(
                                    productType
                                )
                            ) {
                                return false;
                            }
                            return true;
                        })
                );
            })
            .catch(() => {
                // DO NOTHING
            });
    }, []);

    const renderSmallWidget = (ing, binary, storeId) =>
        ing && (
            <SmallWidget
                key={`${ing.ingredient.product_id}-${ing.isFree ? "free" : ""}`}
                binary={binary}
                transparentImage={false}
                isFree={ing.isFree}
                ingredient={ing.ingredient}
                amount={ing.amount}
                onAdd={() => handleAmountChange(ing.ingredient, 1)}
                onRemove={() => handleAmountChange(ing.ingredient, -1)}
                storeId={storeId}
            />
        );

    const handleAmountChange = useCallback(
        (item, value) => {
            if (value > 0) {
                const ingredientCats = item.categories;
                const isBase = !ingredientCats.find(
                    (cat) => !cat.startsWith(BASE_CAT)
                );

                const objectToAdd = {
                    ingredient: item,
                    timestamp: Date.now(),
                    amount: 1,
                    isFree: isBase,
                };

                // if this is a base
                if (isBase) {
                    if (configuratorRules.included[BASE_CAT] === 2) {
                        setSelectedIngredients((old) => {
                            const currentBases = old.filter((ingredient) =>
                                isBaseFilter(ingredient.ingredient)
                            );
                            const baseAmount = currentBases.length;
                            if (baseAmount === 0) {
                                return [...old, { ...objectToAdd, amount: 2 }];
                            } else {
                                const baselessOld = old.filter(
                                    (ingredient) =>
                                        !isBaseFilter(ingredient.ingredient)
                                );
                                if (baseAmount === 1) {
                                    return [
                                        ...baselessOld,
                                        { ...currentBases[0], amount: 1 },
                                        { ...objectToAdd, amount: 1 },
                                    ];
                                } else {
                                    return [
                                        ...baselessOld,
                                        { ...currentBases[1], amount: 1 },
                                        { ...objectToAdd, amount: 1 },
                                    ];
                                }
                            }
                        });
                    } else {
                        setSelectedIngredients((old) => [
                            ...old.filter(
                                (ing) => !isBaseFilter(ing.ingredient)
                            ),
                            objectToAdd,
                        ]);
                    }
                    return;
                }

                setSelectedIngredients((old) => {
                    const ingredientsWithoutBaseDressingsAndToppings =
                        old.filter(
                            (ing) =>
                                !isBaseFilter(ing.ingredient) &&
                                !isDressingFilter(ing.ingredient) &&
                                !isToppingFilter(ing.ingredient)
                        );
                    const isDressing = !ingredientCats.find(
                        (cat) => cat !== DRESSING_CAT
                    );
                    const isTopping = !ingredientCats.find(
                        (cat) => cat !== TOPPING_CAT
                    );

                    if (!isDressing && !isBase && !isTopping) {
                        // check if maximum ingredients are reached
                        if (
                            ingredientsWithoutBaseDressingsAndToppings.length >=
                            configuratorRules.maxIngredients
                        ) {
                            setError(ERROR.MAX_TOTAL_INGREDIENTS);
                            return old;
                        }

                        // check if maximum single position is reached
                        const sameIngredients = old.filter(
                            (ing) =>
                                ing.ingredient.product_id === item.product_id
                        );
                        if (
                            sameIngredients.length >=
                            configuratorRules.maxSingle
                        ) {
                            setError(ERROR.MAX_SINGLE_INGREDIENTS);
                            return old;
                        }
                    }
                    return [...old, objectToAdd];
                });
            } else {
                setSelectedIngredients((old) => {
                    const pos = old.indexOf(
                        old.find(
                            (ing) =>
                                ing.ingredient.product_id === item.product_id
                        )
                    );
                    const itemRemovedIngredients = [
                        ...old.slice(0, pos),
                        ...old.slice(pos + 1),
                    ];
                    const ingredientCats = item.categories;
                    const isBase = !ingredientCats.find(
                        (cat) => !cat.startsWith(BASE_CAT)
                    );
                    if (isBase && configuratorRules.included[BASE_CAT] === 2) {
                        const remainingBase = itemRemovedIngredients.find(
                            (ingredient) => isBaseFilter(ingredient.ingredient)
                        );
                        if (remainingBase) {
                            const baselessIngredients =
                                itemRemovedIngredients.filter(
                                    (ingredient) =>
                                        !isBaseFilter(ingredient.ingredient)
                                );
                            return [
                                ...baselessIngredients,
                                { ...remainingBase, amount: 2 },
                            ];
                        }
                    }
                    return itemRemovedIngredients;
                });
            }
            scrollHandler();
        },
        [selectedIngredients, ingredients]
    );

    // sets the "free" flag to free ingredients and combines equal ingredients
    useEffect(() => {
        let out = [];

        const getFree = (filter, threshold) => {
            return selectedIngredients
                .filter((ing) => filter(ing.ingredient))
                .sort((a, b) => (a.timestamp < b.timestamp ? -1 : 1))
                .sort((a, b) =>
                    a.ingredient.price < b.ingredient.price ? -1 : 1
                )
                .slice(0, threshold);
        };

        const freeStuff = [
            ...getFree(
                isStandardFilter,
                configuratorRules.included[STANDARD_CAT]
            ),
            ...getFree(isExtraFilter, configuratorRules.included[EXTRA_CAT]),
            ...getFree(
                isPremiumFilter,
                configuratorRules.included[PREMIUM_CAT]
            ),
            ...getFree(
                isToppingFilter,
                configuratorRules.included[TOPPING_CAT]
            ),
            ...getFree(
                isDressingFilter,
                configuratorRules.included[DRESSING_CAT]
            ),
            ...getFree(isSauceFilter, configuratorRules.included[SAUCE_CAT]),
        ];

        for (let i = 0; i < selectedIngredients.length; i++) {
            const obj = { ...selectedIngredients[i] };

            const addOrIncrease = () => {
                const alreadyInThere = out.find(
                    (ing) =>
                        ing.ingredient.product_id ===
                            obj.ingredient.product_id &&
                        ing.isFree === obj.isFree
                );
                if (alreadyInThere) {
                    alreadyInThere.amount++;
                } else {
                    out.push(obj);
                }
            };

            if (freeStuff.find((ing) => ing === selectedIngredients[i])) {
                obj.isFree = true;
                addOrIncrease();
            } else {
                addOrIncrease();
            }
        }

        setSelectedIngredientsWithDetails(out);
    }, [selectedIngredients]);

    // order categories
    let categories = [
        productTypes[productType].base,
        STANDARD_CAT,
        ...chippedIngredients
            .reduce((prev, curr) => {
                let newCats = [];
                for (const ic of curr.categories) {
                    if (!prev.find((x) => x === ic)) {
                        // @ts-ignore
                        newCats = [...newCats, ic];
                    }
                }
                return [...prev, ...newCats];
            }, [])
            .sort((a, b) => (a < b ? -1 : 1))
            .filter(
                (x) =>
                    x !== DRESSING_CAT &&
                    x !== STANDARD_CAT &&
                    x !== SAUCE_CAT &&
                    !x.startsWith(BASE_CAT)
            ),
        DRESSING_CAT,
        SAUCE_CAT,
    ].map((category) => {
        const basedCategory = category.startsWith("basis") ? "basis" : category;
        return {
            name: i18n.t(
                `configurator.ingredientMainCategories.${category}.title`,
                { defaultValue: category, count: 2 }
            ),
            description: createDescription(
                category,
                configuratorRules.included[basedCategory],
                isSauceProduct
            ),
            id: i18n.t(`configurator.ingredientMainCategories.${category}.id`, {
                defaultValue: category,
            }),
        };
    });

    const noIngredientsFound = chippedIngredients.length === 0 && (
        <div style={{ textAlign: "center" }}>
            Wir haben leider keine passenden Zutaten gefunden. Suche nach einer
            Anderen.
        </div>
    );

    const soldOutCheck = (item) => {
        if (storeId === 0) return false;
        if (ingredientsInStock.isLoading) return false;
        if (window.isPhroshDev) return Math.random() > 0.8;
        return !stocks.find(
            (stock) => stock.product_id === item.product_id && stock.available
        );
    };

    const renderCategories = categories.map((cat) => {
        const availableIngredientsInCat = chippedIngredients.filter(
            (ingr) =>
                ingr.categories.find((tag) => tag === cat.id) &&
                !soldOutCheck(ingr)
        );

        if (availableIngredientsInCat.length < 1) return null;

        return (
            <>
                <CatName id={`cat-${cat.id}`}>
                    {cat.name} ({availableIngredientsInCat.length})
                    <CatSubheader>{cat.description}</CatSubheader>
                </CatName>
                <IngredientList viewport={viewport}>
                    {availableIngredientsInCat.map((item) => {
                        const binary = isBaseFilter(item);
                        return (
                            <Widget
                                binary={!!binary}
                                transparentImage={false}
                                key={item.product_id}
                                ingredient={item}
                                amount={
                                    selectedIngredients.filter(
                                        (ing) =>
                                            ing.ingredient.product_id ===
                                            item.product_id
                                    ).length
                                }
                                storeId={storeId}
                                onAdd={() => handleAmountChange(item, 1)}
                                onReset={() =>
                                    setSelectedIngredients((old) => {
                                        const itemRemovedIngredients =
                                            old.filter(
                                                (ing) =>
                                                    ing.ingredient
                                                        .product_id !==
                                                    item.product_id
                                            );
                                        if (
                                            binary &&
                                            configuratorRules.included[
                                                BASE_CAT
                                            ] === 2
                                        ) {
                                            const remainingBase =
                                                itemRemovedIngredients.find(
                                                    (ingredient) =>
                                                        isBaseFilter(
                                                            ingredient.ingredient
                                                        )
                                                );
                                            if (remainingBase) {
                                                const baselessIngredients =
                                                    itemRemovedIngredients.filter(
                                                        (ingredient) =>
                                                            !isBaseFilter(
                                                                ingredient.ingredient
                                                            )
                                                    );
                                                return [
                                                    ...baselessIngredients,
                                                    {
                                                        ...remainingBase,
                                                        amount: 2,
                                                    },
                                                ];
                                            }
                                        }
                                        return itemRemovedIngredients;
                                    })
                                }
                                onRemove={() => handleAmountChange(item, -1)}
                            />
                        );
                    })}
                </IngredientList>
            </>
        );
    });

    const chips = (
        <Chips viewport={viewport}>
            {CHIPS.map((chip) => (
                <Chip
                    key={chip.value}
                    icon={chip.icon}
                    value={chip.text}
                    selected={chipsSelected.find((x) => x === chip.value)}
                    onUnselect={() =>
                        setChipsSelected((old) =>
                            old.filter((x) => x !== chip.value)
                        )
                    }
                    onSelect={() =>
                        setChipsSelected((old) => [...old, chip.value])
                    }
                />
            ))}
        </Chips>
    );

    const scrollToSearchBar = () => {
        if (!ingredients || !stocks) return;
        const container = document.getElementById("left");
        if (container) {
            window.scrollTo({
                behavior: "smooth",
                top: container.offsetTop,
            });
        }
    };

    const searchBar = (
        <SearchBar
            id="search-bar"
            placeholder="Suche bspw. nach Käse, Mozzarella etc."
            data={ingredients}
            searchKeys={["name", "infos"]}
            autoCompleteKey="name"
            ingredients={ingredients}
            onClear={() => {
                setFilteredIngredients(ingredients);
            }}
            onFocus={(value) => {
                if (!viewport.isDesktop) {
                    scrollToSearchBar();
                }
                setHideIngredients(!value || value.length === 0);
            }}
            onBlur={() => {
                setHideIngredients(false);
            }}
            onSearch={(results) => {
                if (viewport.isDesktop) {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                } else if (results.length > 0) {
                    const container = document.getElementById("left");
                    window.scrollTo({
                        top: container.offsetTop - 20,
                        behavior: "auto",
                    });
                    scrollToSearchBar();
                }
                setHideIngredients(false);
                if (results.length > 0) {
                    setFilteredIngredients(results.map((item) => item.item));
                } else {
                    setFilteredIngredients(ingredients);
                }
            }}
        />
    );

    const addedToCartPopup = !window.isPhroshDev && (
        <AddToCartSuccess
            open={addedToCartPopupOpen}
            productName={i18n.t("configurator.addToCartSuccess." + productType)}
            productImage={productTypes[productType].image}
            onClose={() => setAddedToCartPopupOpen(false)}
            onRedirect={() => {
                setAddedToCartPopupOpen(false);
                window.location.href = "/warenkorb/";
            }}
        />
    );

    const rightContent = (
        <RightContent id="right-content">
            <RightTop className="bottom-dweller-grab">
                <RightTopElement>
                    <div className="top">
                        <div className="icon">
                            <img src={Fork} />
                            <img src={Knife} />
                        </div>
                        Brennwert
                    </div>
                    <div className="bottom">
                        {Math.round(
                            selectedIngredients.reduce(
                                (prev, curr) =>
                                    prev +
                                    getTotalEnergy(curr.ingredient) *
                                        curr.amount,
                                0
                            )
                        )}{" "}
                        kcal
                    </div>
                </RightTopElement>
                <RightTopElement>
                    <div className="top">
                        <div className="icon">
                            <img src={Globe} />
                        </div>
                        CO<sub>2</sub>
                    </div>
                    <div className="bottom">
                        {formatFloat(
                            Math.round(
                                selectedIngredients.reduce(
                                    (prev, curr) =>
                                        prev +
                                        curr.ingredient.details.co2 *
                                            curr.amount,
                                    0
                                ) / 10
                            ) / 100
                        )}{" "}
                        kg
                    </div>
                </RightTopElement>
                <RightTopElement>
                    <div className="top">
                        <div className="icon">
                            <img src={Water} />
                        </div>
                        Wasser
                    </div>
                    <div className="bottom">
                        {formatFloat(
                            Math.round(
                                selectedIngredients.reduce(
                                    (prev, curr) =>
                                        prev +
                                        curr.ingredient.details.water *
                                            curr.amount,
                                    0
                                ) / 10
                            ) / 100
                        )}{" "}
                        l
                    </div>
                </RightTopElement>
            </RightTop>
            <ChosenOnes>Ausgewählte Zutaten ({totalIngredients})</ChosenOnes>

            {selectedIngredients.length === 0 ? (
                <InlineNotification
                    big
                    type="info"
                    text="Du hast noch keine Zutat ausgewählt"
                />
            ) : (
                <SmallWidgetContainer id="small-widget-container">
                    {selectedIngredientsWithDetails
                        .filter((ing) => isBaseFilter(ing.ingredient))
                        .sort((a, b) =>
                            a.ingredient.name < b.ingredient.name ? -1 : 1
                        )
                        .map((ing) => renderSmallWidget(ing, true, storeId))}
                    {selectedIngredientsWithDetails
                        .filter((ing) => isStandardFilter(ing.ingredient))
                        .sort((a, b) =>
                            a.ingredient.name < b.ingredient.name ? -1 : 1
                        )
                        .map((ing) => renderSmallWidget(ing, false, storeId))}
                    {selectedIngredientsWithDetails
                        .filter(isAnythingElseFilter)
                        .sort((a, b) =>
                            a.ingredient.name < b.ingredient.name ? -1 : 1
                        )
                        .map((ing) => renderSmallWidget(ing, false, storeId))}
                    {selectedIngredientsWithDetails
                        .filter((ing) => isDressingFilter(ing.ingredient))
                        .sort((a, b) =>
                            a.ingredient.name < b.ingredient.name ? -1 : 1
                        )
                        .map((ing) => renderSmallWidget(ing, false, storeId))}
                    {selectedIngredientsWithDetails
                        .filter((ing) => isSauceFilter(ing.ingredient))
                        .sort((a, b) =>
                            a.ingredient.name < b.ingredient.name ? -1 : 1
                        )
                        .map((ing) => renderSmallWidget(ing, false, storeId))}
                </SmallWidgetContainer>
            )}
        </RightContent>
    );
    const includedList = Object.entries(configuratorRules.included).reduce(
        (arr, [key, value]) => {
            if (value > 0) {
                const productKey =
                    key === BASE_CAT && productType !== "salad"
                        ? `basis-${productType}`
                        : key;
                arr.push(
                    i18n.t(
                        "configurator.ingredientMainCategories." +
                            productKey +
                            ".name",
                        {
                            count: value,
                            amount: i18n.t(
                                "configurator.ingredientMainCategories.numbers." +
                                    value
                            ),
                        }
                    )
                );
            }
            return arr;
        },
        []
    );

    const includedIngredients = includedList.slice(0, -1).join(", ");

    const subheaderNotice = (
        <SubheaderNotice>
            <strong>
                {includedIngredients.charAt(0).toUpperCase() +
                    includedIngredients.slice(1)}
            </strong>{" "}
            und <strong>{includedList.at(-1)}</strong> sind im Basispreis{" "}
            {productBase?.product &&
                "von " + formatPrice(productBase.product?.value_gross) + " "}
            inbegriffen.{" "}
            {i18n.t("configurator.instruction", {
                count: configuratorRules.included[BASE_CAT],
            })}
        </SubheaderNotice>
    );

    const addToCart = (
        <>
            <Bottom>
                <div className="grouptotal">
                    <div className="total">Gesamtsumme</div>
                    <div className="total">{price} </div>
                </div>
                <div className="grouptotal">
                    <div className="vat">(inkl. MwSt.)</div>
                    {hasGreenClubDiscount && viewport.isDesktop && (
                        <GreenClubTag />
                    )}
                </div>
            </Bottom>

            <BottomButtonContainer>
                <DefaultButton
                    onClick={handleAddToCart}
                    disabled={totalIngredients === 0}
                    icon={Basket}
                    block
                    appearance="primary"
                    size="lg"
                    loading={loading.find((x) => x === "add-to-cart")}
                    iconScale={1.5}>
                    In den Warenkorb
                </DefaultButton>
            </BottomButtonContainer>
        </>
    );

    const errors = (
        <>
            <Portal
                disabled={
                    !!loading.find(
                        (x) =>
                            x === "overlays" ||
                            x === "stocks" ||
                            x === "ingredients"
                    )
                }
                show={!!error}
                onHide={() => setError(null)}>
                {alertFromError()}
            </Portal>
            {addedToCartPopup}
        </>
    );

    if (greenClubLoading) {
        return <FullPageLoader />;
    }

    if (viewport.isMobile || viewport.isTablet)
        return (
            <>
                {subheaderNotice}
                <Affix top={0}>{searchBar}</Affix>
                {hideIngredients ? (
                    <div style={{ height: "100vh", flex: 1 }} />
                ) : null}
                <Container
                    id="left"
                    style={
                        hideIngredients
                            ? {
                                  height: 0,
                                  overflow: "hidden",
                                  opacity: 0,
                              }
                            : {}
                    }>
                    {chips}
                    {noIngredientsFound}
                    {renderCategories}
                    <BottomDweller
                        overlay={
                            <MobileMiniCart
                                id="mobile-mini-cart"
                                dwellerExpanded={!showMiniCheckout}>
                                <img
                                    src={productTypes[productType].image}
                                    className="image"
                                />
                                <div className="content">
                                    <div className="price">{price}</div>
                                    <div>
                                        {totalIngredients} Zutat
                                        {totalIngredients === 1
                                            ? ""
                                            : "en"}{" "}
                                        ausgewählt
                                    </div>
                                </div>
                                <DefaultButton
                                    className="default-button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleAddToCart();
                                    }}
                                    onTouchMove={(e) => {
                                        e.stopPropagation();
                                    }}
                                    onTouchStart={(e) => {
                                        e.stopPropagation();
                                    }}
                                    onTouchEnd={(e) => {
                                        e.stopPropagation();
                                    }}
                                    disabled={totalIngredients === 0}
                                    icon={Basket}
                                    appearance="primary"
                                    size="lg"
                                    loading={loading.find(
                                        (x) => x === "add-to-cart"
                                    )}
                                    iconScale={1.75}
                                    iconOnly
                                />
                            </MobileMiniCart>
                        }
                        disabled={
                            !!loading.find(
                                (x) =>
                                    x === "overlays" ||
                                    x === "stocks" ||
                                    x === "ingredients"
                            )
                        }
                        onSizeChange={(perc) => {
                            const addToMobileCart =
                                document.getElementById("add-to-mobile-cart");
                            if (addToMobileCart) {
                                addToMobileCart.style.height = `${perc}px`;
                            }
                            setShowMiniCheckout(perc < 100);
                        }}
                        onExpand={() => {
                            const rightContent =
                                document.getElementById("right-content");
                            if (rightContent)
                                rightContent.scrollTo({
                                    top: 0,
                                    behavior: "auto",
                                });
                            setDwellerExpanded(true);
                        }}
                        onCollapse={() => {
                            const rightContent =
                                document.getElementById("right-content");
                            if (rightContent)
                                rightContent.scrollTo({
                                    top: 0,
                                    behavior: "smooth",
                                });
                            setDwellerExpanded(false);
                        }}
                        expanded={dwellerExpanded}>
                        <MobileCart>
                            <div style={{ flex: 1, overflow: "hidden" }}>
                                {rightContent}
                            </div>
                            <AddToMobileCart id="add-to-mobile-cart">
                                {addToCart}
                            </AddToMobileCart>
                        </MobileCart>
                    </BottomDweller>
                </Container>
                {errors}
            </>
        );

    if (viewport.isDesktop)
        return (
            <>
                <OuterContainer>
                    <Left id="left">
                        {subheaderNotice}
                        <Affix top={0}>{searchBar}</Affix>
                        <Container
                            style={
                                hideIngredients
                                    ? {
                                          height: 0,
                                          overflow: "hidden",
                                          opacity: 0,
                                      }
                                    : {}
                            }>
                            {chips}
                            {noIngredientsFound}
                            {renderCategories}
                        </Container>
                    </Left>
                    <Right id="right">
                        <Affix top={10}>
                            <StickyRight id="sticky-right">
                                {rightContent}
                            </StickyRight>
                        </Affix>
                        <BottomRightContainer id="bottom-right-container">
                            {addToCart}
                        </BottomRightContainer>
                    </Right>
                    {errors}
                </OuterContainer>
                {addedToCartPopup}
            </>
        );
};

IngredientPage.defaultProps = {
    productType: "salad",
};

IngredientPage.propTypes = {
    productId: PropTypes.number,
    productType: PropTypes.string,
    config: PropTypes.any,
};

export default (props) => (
    <DeliveryProvider>
        <IngredientPage {...props} />
    </DeliveryProvider>
);
